import { AnimatePresence, motion } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import { useTypes } from '@/framework/type';
import { Routes } from '@/config/routes';
import { TYPES_PER_PAGE } from '@/framework/client/variables';
import { ArrowNext, ArrowPrev } from '../icons';
import Link from '../ui/link';
import { useCategories } from '@/framework/category';
import CategoriesLoader from '../ui/loaders/categories-loader';
import useClickOutside from '@/lib/hooks/use-click-outside';
import { CloseIcon } from '../icons/close-icon';
import { twMerge } from 'tailwind-merge';
import dynamic from 'next/dynamic';
import { trackCategoryView } from '@/lib/gtm';
import { rewriteStaticAssetUrl } from '@/lib/static-asset-url';

const InAppNotificationBell = dynamic(
  () => import('@/components/notifications/in-app-notification-bell'),
  { ssr: false },
);

type CategoriesRowProps = {
  isAuthorize?: boolean;
};

const CategoriesRow = ({ isAuthorize = false }: CategoriesRowProps) => {
  const [showCategories, setShowCategories] = useState(false);
  const [activeParent, setActiveParent] = useState('Medicamentos');
  const [activeParentSlug, setActiveParentSlug] = useState<string>('');

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  useClickOutside(
    [dropdownRef, buttonRef],
    () => setShowCategories(false),
    showCategories,
  );

  const { types }: any = useTypes({
    limit: TYPES_PER_PAGE,
  });

  const filteredTypes = types?.filter((type: any) => type.id !== 20) || [];
  const inlineTypes = filteredTypes.slice(0, 5);

  // Set initial active parent when types are loaded
  useEffect(() => {
    if (filteredTypes.length > 0 && !activeParentSlug) {
      const firstType = filteredTypes[0];
      setActiveParent(firstType.name);
      setActiveParentSlug(firstType.slug);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTypes.length, activeParentSlug]);

  const { categories, isLoading } = useCategories({
    type: activeParentSlug,
    with: 'children',
    limit: 100
  });

  const activeParentData = filteredTypes.find(
    (type: any) => type.slug === activeParentSlug,
  );

  const hasMenuContent = Boolean(
    activeParentData?.settings?.menu &&
    (Boolean(activeParentData.settings.menu.best_brands?.length) ||
      Boolean(activeParentData.settings.menu.promotion) ||
      Boolean(activeParentData.settings.menu.banner)),
  );

  const closeMenu = () => setShowCategories(false);

  return (
    <div
      id="categories-row"
      className="hidden lg:flex w-full justify-center px-5 py-2 relative bg-primary"
    >
      <div ref={dropdownRef} className="flex w-[75%] flex-col relative">
        <div className="flex items-center justify-between gap-x-6">
          <button type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowCategories((prev) => !prev);
            }}
            ref={buttonRef}
            className={`group flex items-center gap-x-2 text-normal font-medium px-3 py-1 rounded-xl transition-colors ${showCategories
              ? 'text-accent bg-white'
              : 'text-white hover:text-accent hover:bg-white'
              }`}
          >
            {/* Hamburger */}
            <div className="flex flex-col gap-[3px]">
              <span
                className={`block h-0.5 w-4 rounded transition-colors ${showCategories
                  ? 'bg-accent'
                  : 'bg-white group-hover:bg-accent'
                  }`}
              />
              <span
                className={`block h-0.5 w-4 rounded transition-colors ${showCategories
                  ? 'bg-accent'
                  : 'bg-white group-hover:bg-accent'
                  }`}
              />
              <span
                className={`block h-0.5 w-3 rounded transition-colors ${showCategories
                  ? 'bg-accent'
                  : 'bg-white group-hover:bg-accent'
                  }`}
              />
            </div>
            {"Categorias"}
          </button>

          <div className="flex flex-1 items-center justify-around gap-x-5 text-normal font-medium">
            {inlineTypes.map((type: any) => (
              <Link
                key={type.id}
                href={`/${type?.slug}/${type?.id}`}
                onClick={closeMenu}
                className="text-white hover:text-accent hover:bg-white px-2 py-1 rounded-xl transition-colors"
              >
                {type.name}
              </Link>
            ))}
          </div>

          <div className="flex shrink-0 items-center gap-x-3">
            <Link
              href={Routes.downloadApp}
              className="native-hide-in-app rounded-xl px-3 py-1 font-medium text-white transition-colors hover:bg-white hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-accent"
            >
              Baixe o app
            </Link>
            <InAppNotificationBell isAuthorize={isAuthorize} />
          </div>
        </div>

        {/* Mega Dropdown Menu */}
        <AnimatePresence>
          {showCategories && (
            <motion.div
              ref={dropdownRef}
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
              className="absolute top-full left-0 mt-6 w-full bg-white shadow-lg rounded-xl pt-8 p-6 z-40 border border-border-200"
            >
              <button type="button"
                onClick={() => setShowCategories(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-gray-800 text-lg"
              >
                <CloseIcon className="h-5 w-5" />
              </button>

              <div className="mb-6 relative">
                <Swiper
                  slidesPerView="auto"
                  spaceBetween={12}
                  navigation={{
                    nextEl: '.swiper-next',
                    prevEl: '.swiper-prev',
                  }}
                  modules={[Navigation]}
                  freeMode={true}
                  grabCursor={true}
                  className="w-full"
                >
                  {filteredTypes.map((type: any) => (
                    <SwiperSlide key={type.id} style={{ width: 'auto' }}>
                      <button type="button"
                        onClick={() => {
                          setActiveParent(type.name);
                          setActiveParentSlug(type.slug);
                        }}
                        aria-label={`Filtrar categorias por ${type.name}`}
                        className={`whitespace-nowrap px-4 py-1 text-normal font-medium rounded-xl transition-colors ${activeParent === type.name
                          ? 'text-accent underline underline-offset-4'
                          : 'text-gray-700 hover:text-accent'
                          }`}
                      >
                        {type.name}
                      </button>
                    </SwiperSlide>
                  ))}
                </Swiper>

                <button type="button" className="swiper-prev absolute -left-4 top-1/2 -translate-y-1/2 z-10 bg-white p-1 rounded-full shadow text-accent">
                  <span className="sr-only">{"Anterior"}</span>
                  <ArrowPrev width={18} height={18} />
                </button>
                <button type="button" className="swiper-next absolute -right-4 top-1/2 -translate-y-1/2 z-10 bg-white p-1 rounded-full shadow text-accent">
                  <span className="sr-only">{"Próximo"}</span>
                  <ArrowNext width={18} height={18} />
                </button>
              </div>

              <div className="flex gap-8 px-4">
                <div
                  className={twMerge(
                    'max-h-[520px] overflow-y-auto pr-2 pb-2',
                    hasMenuContent ? 'flex-1 min-w-0' : 'w-full',
                  )}
                >
                  {isLoading ? (
                    <div className="col-span-2 text-gray-500 max-h-52 overflow-hidden">
                      <CategoriesLoader />
                    </div>
                  ) : (
                    <div
                      className="grid gap-x-8 gap-y-3 text-sm"
                      style={{
                        gridTemplateColumns:
                          'repeat(auto-fill, minmax(180px, 1fr))',
                      }}
                    >
                      {categories?.map((category: any) => (
                        <Link
                          key={category.id}
                          href={Routes.category(category.slug)}
                          onClick={() => {
                            trackCategoryView(category.name);
                            closeMenu();
                          }}
                          className="text-gray-700 hover:text-accent whitespace-nowrap"
                        >
                          {category.name}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>

                {hasMenuContent && (
                  <div className="flex gap-4 shrink-0 max-w-[400px] w-full">
                    {/* Left side: best_brands and promotion */}
                    <div className="flex flex-col gap-4 justify-around flex-1 min-w-0">
                      {/* Best brands - 3 images in a row */}
                      {activeParentData.settings.menu.best_brands &&
                        activeParentData.settings.menu.best_brands.length >
                        0 && (
                          <div className="flex flex-col gap-2 shrink-0">
                            <p className="text-sm font-medium text-gray-800">
                              Melhores Marcas
                            </p>
                            <div
                              className="flex gap-2"
                              style={{ height: '60px' }}
                            >
                              {activeParentData.settings.menu.best_brands
                                .slice(0, 3)
                                .map((brand: any, index: number) => (
                                  <Link
                                    key={brand.id || index}
                                    href={brand.url || '#'}
                                    onClick={closeMenu}
                                    className="flex-1 h-full overflow-hidden rounded-full"
                                  >
                                    <img
                                      src={rewriteStaticAssetUrl(brand.original || brand.thumbnail)}
                                      alt={`Marca ${index + 1}`}
                                      className="w-full h-full object-fit rounded-full"
                                    />
                                  </Link>
                                ))}
                            </div>
                          </div>
                        )}

                      {/* Promotion image */}
                      {activeParentData.settings.menu.promotion && (
                        <div className="flex flex-col">
                          <p className="text-sm font-medium text-gray-800 mb-2">
                            Promoção
                          </p>

                          <Link
                            href={
                              activeParentData.settings.menu.promotion.url ||
                              '#'
                            }
                            onClick={closeMenu}
                            className="inline-block"
                          >
                            <img
                              src={rewriteStaticAssetUrl(
                                activeParentData.settings.menu.promotion
                                  .original ||
                                activeParentData.settings.menu.promotion
                                  .thumbnail,
                              )}
                              alt="Promoção"
                              className="max-h-[280px] w-auto max-w-full rounded-xl object-contain"
                            />
                          </Link>
                        </div>
                      )}
                    </div>

                    {/* Right side: Banner - full height */}
                    {activeParentData.settings.menu.banner && (
                      <div className="w-[200px] shrink-0 self-stretch">
                        <Link
                          href={
                            activeParentData.settings.menu.banner.url || '#'
                          }
                          onClick={closeMenu}
                          className="block h-full overflow-hidden rounded-lg"
                        >
                          <img
                            src={rewriteStaticAssetUrl(
                              activeParentData.settings.menu.banner.original ||
                              activeParentData.settings.menu.banner.thumbnail,
                            )}
                            alt={
                              activeParentData.settings.menu.banner.title ||
                              'Banner'
                            }
                            className="w-full h-full object-contain rounded-lg"
                          />
                        </Link>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {activeParentData && (
                <div className="mt-4 pt-3 border-t border-border-200 px-4 flex justify-end">
                  <Link
                    href={`/${activeParentData?.slug}/${activeParentData?.id}`}
                    onClick={closeMenu}
                    aria-label={`Acessar ${activeParentData.name}`}
                    className="text-sm font-medium text-gray-700 hover:text-accent transition-colors underline underline-offset-4"
                  >
                    {`Acessar ${activeParentData.name}`}
                  </Link>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default CategoriesRow;
